import { useQuery } from '@apollo/client';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Divider, FluentProvider, webLightTheme } from '@fluentui/react-components';
import {
  cardContentWrapperStyles,
  dividerStyles,
} from '../../../app/common/styles/CommonStyleObjects';
import {
  GET_COMPANY_MILESTONE_DOCUMENTS,
  GET_COMPANY_DOCUMENT_FILTERS,
} from '../../../utils/statApi/CompaniesApi';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import { ICompanyCodeParams } from '../../common/ParamTypes';
import {
  multiDropdownLargeWrapper,
  multiDropdownMediumWrapper,
  multiDropdownSmallWrapper,
} from './CompanyDocumentStyles';
import DocumentsList from '../../common/documents/DocumentsList';
import ICompanyMilestoneDocument from '../../../utils/types/ICompanyMilestoneDocument';
import {
  getInitialPageSize,
  getTotalPageCount,
} from '../../common/DetailsLists/pagination/DetailsListPaginationBanner';
import IPaginationMetadata from '../../common/DetailsLists/pagination/IPaginationMetadata';
import MultiSelectDropdown from '../../common/MultiSelectDropdown';
import IDropdownOption from '../../../utils/types/IDropdownOption';
import FeatureFlagContext from '../../../utils/featureFlags/FeatureFlagContext';
import UserContext from '../../../utils/authorization/UserContext';
import hasAccess from '../../../utils/authorization/authorizationCheck';
import ExtractTextButton from './ExtractTextButton';
import { isTextExtractionActive, TEXT_EXTRACTION_POLL_INTERVAL_MS } from './textExtractionStatus';

interface IMilestoneDocumentsProps {
  showDeletedFiles: boolean;
}

const MilestoneDocuments = ({ showDeletedFiles }: IMilestoneDocumentsProps): JSX.Element => {
  const { companyCode } = useParams<ICompanyCodeParams>();
  const featureFlags = useContext(FeatureFlagContext);
  const user = useContext(UserContext);
  const canExtractText =
    featureFlags.flags.TextExtraction === true &&
    hasAccess(user?.permissions, 'DocumentAnalysis:Create');
  const styles = dividerStyles();
  const [fiscalYears, setFiscalYears] = useState([]);
  const [processIds, setProcessIds] = useState([]);
  const [processTypeIds, setProcessTypeIds] = useState([]);
  const [milestoneNameIds, setMilestoneNameIds] = useState([]);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [defaultPageSize, setDefaultPageSize] = useState<number>(getInitialPageSize());

  const { data, loading, error, startPolling, stopPolling } = useQuery(
    GET_COMPANY_MILESTONE_DOCUMENTS,
    {
      variables: {
        paginationDto: {
          currentPage: selectedPage,
          pageSize: defaultPageSize,
        },
        companyCode,
        fiscalYears,
        processIds,
        processTypeIds,
        milestoneNameIds,
        showDeletedFiles,
      },
      fetchPolicy: 'network-only',
    },
  );

  const companyDocuments: ICompanyMilestoneDocument[] = data?.companyMilestoneDocuments.items || [];
  const hasActiveExtraction = companyDocuments.some((document) =>
    isTextExtractionActive(document.textExtractionStatus),
  );

  useEffect(() => {
    if (!hasActiveExtraction) {
      return undefined;
    }
    startPolling(TEXT_EXTRACTION_POLL_INTERVAL_MS);
    return stopPolling;
  }, [hasActiveExtraction, startPolling, stopPolling]);

  const {
    data: filterData,
    loading: filterLoading,
    error: filterError,
  } = useQuery(GET_COMPANY_DOCUMENT_FILTERS, {
    variables: { companyCode, fiscalYears, processIds, processTypeIds },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    setSelectedPage(1);
  }, [fiscalYears, processIds, processTypeIds, milestoneNameIds, showDeletedFiles]);

  const pageCount =
    !loading && !error
      ? getTotalPageCount(data?.companyMilestoneDocuments?.totalCount, defaultPageSize)
      : 1;
  const companyDocumentFiscalYearFilters: IDropdownOption[] =
    filterData?.companyDocumentFilters?.companyDocumentFiscalYearFilters || [];

  const companyDocumentProcessFilters: IDropdownOption[] =
    filterData?.companyDocumentFilters?.companyDocumentProcessFilters || [];

  const companyDocumentProcessTypeFilters: IDropdownOption[] =
    filterData?.companyDocumentFilters?.companyDocumentProcessTypeFilters || [];

  const companyDocumentMilestoneNameFilters: IDropdownOption[] =
    filterData?.companyDocumentFilters?.companyDocumentMilestoneNameFilters || [];

  const paginationMetadata: IPaginationMetadata = {
    pageCount,
    selectedPage,
    pageSize: defaultPageSize,
    loadingData: false,
    onPageChange: (startItemIndex: number, endItemIndex: number, newPageNumber: number): void => {
      setSelectedPage(newPageNumber);
    },
    onPageSizeChange: (newPageSize: string | number): void => {
      setDefaultPageSize(newPageSize as number);
    },
  };

  const onFiscalYearChange = (
    _event: React.FormEvent<HTMLElement>,
    items: IDropdownOption[],
  ): void => {
    const itemIds = items.map((item) => item.key);
    setFiscalYears(itemIds);
    setProcessTypeIds([]);
  };

  const onProcessTypeChange = (
    _event: React.FormEvent<HTMLElement>,
    items: IDropdownOption[],
  ): void => {
    const itemIds = items.map((item) => item.key);
    setProcessTypeIds(itemIds);
    setProcessIds([]);
  };

  const onProcessChange = (
    _event: React.FormEvent<HTMLElement>,
    items: IDropdownOption[],
  ): void => {
    const itemIds = items.map((item) => item.key);
    setProcessIds(itemIds);
    setMilestoneNameIds([]);
  };

  const onMilestoneNameChange = (
    _event: React.FormEvent<HTMLElement>,
    items: IDropdownOption[],
  ): void => {
    const itemIds = items.map((item) => item.key);
    setMilestoneNameIds(itemIds);
  };

  return (
    <div className={cardContentWrapperStyles}>
      <FluentProvider theme={webLightTheme}>
        <Divider className={styles.divider} />
      </FluentProvider>
      <Stack tokens={{ childrenGap: 10 }} horizontal wrap>
        <LoadingErrorMessage error={filterError} loading={filterLoading} />
        <MultiSelectDropdown
          label="Fiscal Year"
          aria-label="Filter by fiscal year"
          className={multiDropdownSmallWrapper}
          onChange={onFiscalYearChange}
          selectedKeys={fiscalYears?.map((id) => id.toString())}
          placeholder="Select an option"
          options={companyDocumentFiscalYearFilters}
        />

        <MultiSelectDropdown
          label="Process Type"
          aria-label="Filter by process type"
          className={multiDropdownMediumWrapper}
          onChange={onProcessTypeChange}
          selectedKeys={processTypeIds?.map((id) => id.toString())}
          placeholder="Select an option"
          options={companyDocumentProcessTypeFilters}
        />

        <MultiSelectDropdown
          label="Process"
          aria-label="Filter by process"
          className={multiDropdownSmallWrapper}
          onChange={onProcessChange}
          selectedKeys={processIds?.map((id) => id.toString())}
          placeholder="Select an option"
          options={companyDocumentProcessFilters}
        />

        <MultiSelectDropdown
          label="Milestone Name"
          aria-label="Filter by milestone name"
          className={multiDropdownLargeWrapper}
          onChange={onMilestoneNameChange}
          selectedKeys={milestoneNameIds?.map((id) => id.toString())}
          placeholder="Select an option"
          options={companyDocumentMilestoneNameFilters}
        />
      </Stack>
      <br />
      <DocumentsList
        loading={loading}
        error={error}
        docType="Milestone"
        documents={companyDocuments}
        showDeletedFiles={showDeletedFiles}
        paginationData={paginationMetadata}
        renderDocumentAction={
          canExtractText
            ? (document): JSX.Element =>
                !document.isDeleted && document.documentId ? (
                  <ExtractTextButton
                    key={document.id}
                    companyCode={companyCode}
                    documentId={document.documentId}
                    documentTitle={document.title}
                    hasExtractedText={document.hasExtractedText}
                    textExtractionStatus={document.textExtractionStatus}
                  />
                ) : null
            : undefined
        }
      />
    </div>
  );
};

export default MilestoneDocuments;
